/**
 * Order Creation Service
 * 
 * Специализированный сервис для создания заказов с товарами
 * и автоматическим определением типа заказа (покупка/аренда)
 * 
 * Соблюдает STYLEGUIDE.md:
 * - camelCase для frontend типов
 * - snake_case для database типов
 * - Корректный маппинг между слоями
 */
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { getCurrentUserIdFromStorage } from '@/utils/authTokenSync';
import type { Order, OrderWithDetails, OrderItem, OrderStatus, DbOrder } from '@/types/entities/order';
import type { Database } from '@/types/supabase';
import { mapDbOrderToOrderWithDetails } from '@/shared/mappers/orderMappers';

// Precise DB row types used in this service
type InventoryRow = Database['public']['Tables']['inventory']['Row'];
type OrdersRow = Database['public']['Tables']['orders']['Row'];
type OrdersSelect = OrdersRow & { clients: { id: string; full_name: string | null; company_name: string | null } | null };
interface CreateOrderRpcResult {
  success: boolean;
  order_id: string;
  total_amount: number | null;
  is_store_purchase: boolean;
  items_count: number;
  error?: string;
}
interface CreateOrderRpcArgs {
  order_data: DbOrderData;
  order_items: DbOrderItem[];
}

// Types for order creation (input data, not full OrderItem)
export interface CreateOrderItemData {
  inventoryItemId?: string;
  storeProductId?: string;
  serviceId?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  itemType: 'inventory' | 'store' | 'service';
}

export interface CreateOrderData {
  clientId: string;
  rentalPointId: string;
  returnPointId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  paymentStatus?: string;
  tariffId?: string;
  paymentMethodId?: string;
  promoCode?: string;
  discount?: number;
  depositAmount?: number;
  depositStatus?: string;
  contractGenerated?: boolean;
  notes?: string;
  items: CreateOrderItemData[];
}

// Database types (snake_case) - compatible with Supabase Json type
interface DbOrderItem {
  inventory_id?: string | null; // DB schema: order_items.inventory_id
  store_product_id?: string | null;
  service_id?: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  item_type: string;
  user_id?: string | null; // Add user_id field for RPC compatibility
  [key: string]: unknown; // Index signature for Json compatibility
}

interface DbOrderData {
  client_id: string;
  rental_point_id: string;
  return_point_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  status?: string | null;
  payment_status?: string | null;
  tariff_id?: string | null;
  payment_method_id?: string | null;
  promo_code?: string | null;
  discount_amount?: number | null; // FIXED: используем discount_amount согласно схеме БД
  deposit_amount?: number | null;
  deposit_status?: string | null;
  contract_generated?: boolean | null;
  notes?: string | null;
  order_type?: 'rental' | 'purchase' | 'mixed' | null; // NEW: Add order_type field
  [key: string]: unknown; // Index signature for Json compatibility
}

/**
 * Нормализует статус заказа к допустимым значениям
 */
const normalizeOrderStatus = (status: unknown): OrderStatus => {
  const s = typeof status === 'string' ? (status as string) : undefined;
  if (!s) return 'active'; // По умолчанию новый заказ активен
  // Разрешенные статусы согласно схеме БД
  const allowed: OrderStatus[] = ['pending', 'active', 'completed', 'cancelled', 'overdue'];
  return allowed.includes(s as OrderStatus) ? (s as OrderStatus) : 'active';
};

/**
 * Определяет тип заказа на основе товаров
 */
const determineOrderTypeFromItems = (items: CreateOrderItemData[]): 'rental' | 'purchase' | 'mixed' => {
  // Rental items: inventory items (for rent) or service items
  const hasRental = items.some(item => 
    item.itemType === 'inventory' || 
    item.itemType === 'service' || 
    item.inventoryItemId
  );
  
  // Purchase items: store items
  const hasPurchase = items.some(item => 
    item.itemType === 'store' || 
    item.storeProductId
  );
  
  if (hasRental && hasPurchase) return 'mixed';
  if (hasRental) return 'rental';
  if (hasPurchase) return 'purchase';
  return 'mixed'; // fallback
};

/**
 * Маппер из frontend типов в database типы для order data
 * FIXED: корректный маппинг всех полей согласно реальной схеме БД
 */
const mapOrderDataToDb = (orderData: CreateOrderData): DbOrderData => ({
  client_id: orderData.clientId,
  rental_point_id: orderData.rentalPointId,
  return_point_id: orderData.returnPointId || null,
  start_date: orderData.startDate || null,
  end_date: orderData.endDate || null,
  status: normalizeOrderStatus(orderData.status),
  payment_status: orderData.paymentStatus || 'pending', // FIXED: используем 'pending' как дефолт согласно схеме БД
  tariff_id: orderData.tariffId || null,
  payment_method_id: orderData.paymentMethodId || null,
  promo_code: orderData.promoCode || null,
  discount_amount: orderData.discount || null, // FIXED: используем discount_amount согласно схеме БД
  deposit_amount: orderData.depositAmount || null,
  deposit_status: orderData.depositStatus || 'not_required', // FIXED: используем дефолт согласно схеме БД
  contract_generated: orderData.contractGenerated || false, // FIXED: используем false как дефолт согласно схеме БД
  notes: orderData.notes || null,
  // NEW: Add order_type field based on items
  order_type: determineOrderTypeFromItems(orderData.items),
});

/**
 * Маппер из frontend типов в database типы для order items
 */
const mapOrderItemsToDb = (items: CreateOrderItemData[], userId?: string): DbOrderItem[] => 
  items.map(item => {
    const normalizedType = item.itemType === 'store' ? 'store_product' : item.itemType;
    return {
      inventory_id: item.inventoryItemId || null, // DB schema: order_items.inventory_id
      store_product_id: item.storeProductId || null,
      service_id: item.serviceId || null,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      total_price: item.totalPrice,
      item_type: normalizedType,
      user_id: userId, 
    };
  });

/**
 * Создает заказ с товарами через RPC функцию
 * Автоматически определяет is_store_purchase на основе типов товаров
 */
export const createOrderWithItems = async (orderData: CreateOrderData): Promise<OrderWithDetails> => {
  try {
    // ИСПРАВЛЕНО: используем динамический ключ localStorage вместо жёстко закодированного
    const userId = getCurrentUserIdFromStorage();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Маппинг данных в database формат
    const dbOrderData = mapOrderDataToDb(orderData);
    const dbOrderItems = mapOrderItemsToDb(orderData.items, userId);

    console.log('Creating order with data:', {
      orderData: dbOrderData,
      items: dbOrderItems,
      itemsCount: dbOrderItems.length
    });
    
    // DETAILED LOGGING for debugging foreign key issues
    console.log('DETAILED ORDER DATA:', JSON.stringify(dbOrderData, null, 2));
    console.log('DETAILED ORDER ITEMS:', JSON.stringify(dbOrderItems, null, 2));
    
    // Log each item separately
    dbOrderItems.forEach((item, index) => {
      console.log(`Item ${index + 1}:`, {
        item_type: item.item_type,
        inventory_id: item.inventory_id,
        store_product_id: item.store_product_id,
        service_id: item.service_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      });
    });

    // VALIDATE INVENTORY ITEM OWNERSHIP
    const inventoryItems = dbOrderItems.filter(item => item.inventory_id);
    if (inventoryItems.length > 0) {
      console.log('🔍 VALIDATING INVENTORY ITEM OWNERSHIP...');
      
      for (const item of inventoryItems) {
        console.log(`🔍 Checking inventory item: ${item.inventory_id}`);
        
        const { data: inventoryData, error: inventoryError } = await supabaseWorker
          .from('inventory')
          .select('id, user_id, name')
          .eq('id', item.inventory_id)
          .single();
        
        if (inventoryError) {
          console.error(`❌ Inventory item ${item.inventory_id} not found:`, inventoryError);
          throw new Error(`Inventory item ${item.inventory_id} not found`);
        }
        
        const inv = inventoryData as Pick<InventoryRow, 'id' | 'user_id' | 'name'>;
        console.log(`✅ Inventory item found:`, {
          id: inv.id,
          name: inv.name,
          user_id: inv.user_id,
        });
        
        console.log(`🔍 Current user ID: ${userId}`);
        console.log(`🔍 Inventory owner ID: ${inv.user_id}`);
        
        if (inv.user_id !== userId) {
          console.error(`❌ OWNERSHIP MISMATCH: Inventory item ${item.inventory_id} belongs to user ${inv.user_id}, but current user is ${userId}`);
          throw new Error(`Inventory item "${inv.name}" does not belong to current user`);
        }
        
        console.log(`✅ Ownership verified for inventory item: ${inv.name}`);
      }
      
      console.log('✅ All inventory items ownership validated successfully');
    }

    // Guard: prevent creating order without items (avoids RPC overload ambiguity jsonb vs jsonb[])
    if (!dbOrderItems || dbOrderItems.length === 0) {
      const err = new Error('Нельзя создать заказ без позиций. Добавьте хотя бы один товар магазина или инвентарь.');
      console.error('[createOrderWithItems] Empty order_items detected. Aborting RPC call.', { dbOrderItemsLength: dbOrderItems?.length ?? 0 });
      throw err;
    }

    // CRITICAL DEBUG: Log exact data being sent to RPC
    console.log('🔍 CRITICAL DEBUG - RPC call data:');
    console.log('dbOrderData keys:', Object.keys(dbOrderData));
    console.log('dbOrderData values:', dbOrderData);
    console.log('dbOrderItems length:', dbOrderItems.length);
    
    // CRITICAL FIX: Filter out any undefined or invalid fields before RPC call
    // Note: order_type is now a valid field in DB schema after migration
    const invalidFields = ['orderType', 'type', 'order_category'];
    const cleanOrderData = Object.fromEntries(
      Object.entries(dbOrderData).filter(([key, value]) => {
        // Remove undefined values
        if (value === undefined) return false;
        // Remove invalid fields that cause DB errors
        if (invalidFields.includes(key)) {
          console.warn(`[orderCreationService] Filtering out invalid field: ${key}`);
          return false;
        }
        return true;
      })
    );
    
    console.log('🧹 Cleaned order data:', cleanOrderData);
    console.log('🧹 Cleaned order data keys:', Object.keys(cleanOrderData));

    // Вызов RPC функции для создания заказа с товарами
    const { data, error } = await supabaseWorker.rpc('create_order_with_items_v2', {
      order_data: cleanOrderData as DbOrderData,
      // Explicit array argument for jsonb[] variant on DB side
      order_items: dbOrderItems as DbOrderItem[],
    } as CreateOrderRpcArgs);

    if (error) {
      console.error('Error creating order with items:', error);
      throw error;
    }

    // Проверка результата RPC (строгая типизация)
    const result = data as CreateOrderRpcResult | null;
    if (!result || !result.success) {
      const errorMessage = result?.error || 'Failed to create order';
      console.error('RPC function returned error:', errorMessage);
      throw new Error(errorMessage);
    }

    console.log('Order created successfully:', {
      orderId: result.order_id,
      totalAmount: result.total_amount,
      isStorePurchase: result.is_store_purchase,
      itemsCount: result.items_count
    });

    // Получаем созданный заказ с деталями клиента
    const { data: createdOrder, error: fetchError } = await supabaseWorker
      .from('orders')
      .select('*, clients(id, full_name, company_name)')
      .eq('id', result.order_id)
      .single();

    if (fetchError) {
      console.error('Error fetching created order:', fetchError);
      throw fetchError;
    }

    // Маппинг в frontend тип - добавляем недостающие поля для совместимости
    const created = createdOrder as OrdersSelect;
    // Дополняем обязательные для маппера поля, избегая доступа к полям, которых нет в типе OrdersRow
    const orderWithMissingFields: (OrdersSelect & Pick<DbOrder,
      'inventory' | 'store_items' | 'order_number' | 'return_date' | 'discount_amount' |
      'delivery_type' | 'delivery_address' | 'payment_method' | 'archived_at' | 'archived_by'>) = {
      ...created,
      inventory: null,
      store_items: null,
      order_number: null,
      return_date: null,
      discount_amount: null,
      delivery_type: null,
      delivery_address: null,
      payment_method: null,
      archived_at: null,
      archived_by: null,
    };

    return mapDbOrderToOrderWithDetails(orderWithMissingFields);

  } catch (error) {
    console.error('Error in createOrderWithItems:', error);
    throw error;
  }
};

/**
 * Определяет тип заказа на основе товаров (для валидации)
 */
export const determineOrderType = (items: CreateOrderItemData[]): {
  isStorePurchase: boolean;
  hasInventoryItems: boolean;
  hasStoreItems: boolean;
} => {
  const hasInventoryItems = items.some(item => item.inventoryItemId);
  const hasStoreItems = items.some(item => item.storeProductId);
  const isStorePurchase = hasStoreItems && !hasInventoryItems;

  return {
    isStorePurchase,
    hasInventoryItems,
    hasStoreItems,
  };
};

/**
 * Валидирует данные заказа перед созданием
 */
export const validateOrderData = (orderData: CreateOrderData): string[] => {
  const errors: string[] = [];

  if (!orderData.clientId) {
    errors.push('Client ID is required');
  }

  if (!orderData.rentalPointId) {
    errors.push('Rental Point ID is required');
  }

  if (!orderData.items || orderData.items.length === 0) {
    errors.push('Order must contain at least one item');
  }

  // Валидация товаров
  orderData.items.forEach((item, index) => {
    if (!item.inventoryItemId && !item.storeProductId && !item.serviceId) {
      errors.push(`Item ${index + 1}: must have at least one item ID`);
    }

    if (item.quantity <= 0) {
      errors.push(`Item ${index + 1}: quantity must be greater than 0`);
    }

    if (item.unitPrice < 0) {
      errors.push(`Item ${index + 1}: unit price cannot be negative`);
    }

    if (item.totalPrice < 0) {
      errors.push(`Item ${index + 1}: total price cannot be negative`);
    }
  });

  return errors;
};
